import React, {
  useState,
  useEffect,
} from 'react';
import {
  Box,
  Container,
} from '@material-ui/core';
import { useSnackbar } from 'notistack';

import Page from '../../../components/Layout/Page';
import Header from './Header';
import Results from './Results';

import { getInactiveSubcategoriesPerPage } from '../../../requests/api/subcategories';
import { getAllSupermarkets } from '../../../requests/api/supermarkets';
import { getAllCategories } from '../../../requests/api/categories';

import useStyles from '../SubcategoriesListView/styles';
import alphabeticSort from '../../../utils/alphabeticSort';

function InactiveSubcategoriesListView() {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [count, setCount] = useState(0);
  const [subcategories, setSubcategories] = useState([]);
  const [supermarkets, setSupermarkets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [originalCategories, setOriginalCategories] = useState([]);
  const [allSearchResults, setAllSearchResults] = useState([]);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [query, setQuery] = useState('');
  const [selectedSupermarkets, setSelectedSupermarkets] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);

  const cleanFilters = () => {
    setPage(0);
    setLimit(10);
    setQuery('');
    setSelectedSupermarkets([]);
    setSelectedCategories([]);
  };

  useEffect(() => {
    try {
      const getSupermarkets = async () => {
        const response = await getAllSupermarkets();
        if (response.success) {
          const data = await response?.data?.data;
          setSupermarkets(data.sort((a, b) => alphabeticSort(a, b)));
        } else {
          enqueueSnackbar('Error al cargar la información de los supermercados.', {
            variant: 'error',
          });
        }
      };
      getSupermarkets();
    } catch {
      setSupermarkets([]);
    }
  }, []);

  useEffect(() => {
    try {
      const getCategories = async () => {
        const response = await getAllCategories();
        if (response.success) {
          const data = await response?.data?.data;
          setOriginalCategories(data.sort((a, b) => alphabeticSort(a, b)));
          if (selectedSupermarkets.length > 0) {
            const supIdes = selectedSupermarkets.map((val) => val.id);
            setCategories(data.filter(
              (item) => supIdes.includes(item.supermarket_id),
            ).sort(
              (a, b) => alphabeticSort(a, b),
            ));
          } else {
            setCategories(data.sort((a, b) => alphabeticSort(a, b)));
          }
        } else {
          enqueueSnackbar('Error al cargar la información de las categorías.', {
            variant: 'error',
          });
        }
      };
      getCategories();
    } catch {
      setCategories([]);
    }
  }, [selectedSupermarkets]);

  // Eliminamos la request con limit=10000, usaremos solo los resultados paginados
  // para filtrar los autocompletes
  useEffect(() => {
    setAllSearchResults([]);
  }, [query]);

  useEffect(() => {
    try {
      const getSubcategories = async () => {
        const catIdes = selectedCategories.map((val) => val.id);
        const supIdes = selectedSupermarkets.map((val) => val.id);
        const response = await getInactiveSubcategoriesPerPage(
          page,
          limit,
          query,
          supIdes,
          catIdes,
        );
        if (response.success) {
          const data = response.data?.data || response.data;
          // El backend devuelve subcategories como un objeto o array
          let arrayData = [];
          if (data?.subcategories) {
            if (Array.isArray(data.subcategories)) {
              arrayData = data.subcategories;
            } else {
              arrayData = Object.values(data.subcategories);
            }
          }
          setSubcategories(arrayData);
          setCount(data?.totalSubcategories || 0);
          // Usar los resultados paginados para filtrar autocompletes si hay búsqueda
          if (query && query.trim() !== '') {
            setAllSearchResults(arrayData);
          }
        } else if (response.status === 204) {
          // NO_CONTENT es un caso válido (no hay resultados)
          setSubcategories([]);
          setCount(0);
          if (query && query.trim() !== '') {
            setAllSearchResults([]);
          }
        } else {
          // Solo mostrar error si no es un caso de "no hay resultados"
          enqueueSnackbar('Error al cargar la información de las subcategorías.', {
            variant: 'error',
          });
        }
      };
      getSubcategories();
    } catch (error) {
      enqueueSnackbar('Error al cargar la información de las subcategorías.', {
        variant: 'error',
      });
      setSubcategories([]);
    }
  }, [
    page,
    limit,
    query,
    selectedSupermarkets,
    selectedCategories,
    refreshKey,
  ]);

  return (
    <Page
      className={classes.root}
      title="FH Engel | Subcategorías Eliminadas"
    >
      <Container maxWidth={false}>
        <Header cleanFilters={cleanFilters}/>
        <Box mt={3}>
          <Results
              subcategories={subcategories}
              supermarkets={supermarkets}
              originalCategories={originalCategories}
              categories={categories}
              setCategories={setCategories}
              count={count}
              allSearchResults={allSearchResults}
              page={page}
              limit={limit}
              query={query}
              selectedSupermarkets={selectedSupermarkets}
              selectedCategories={selectedCategories}
              setPage={setPage}
              setLimit={setLimit}
              setQuery={setQuery}
              setSelectedSupermarkets={setSelectedSupermarkets}
              setSelectedCategories={setSelectedCategories}
              onActivate={() => setRefreshKey((prev) => prev + 1)}
            />
        </Box>
      </Container>
    </Page>
  );
}

export default InactiveSubcategoriesListView;
